// src/components/CompanyProfile.tsx — Redesigned Premium UI (matching UserProfile layout)
//
// ── Caching note ──────────────────────────────────────────────────────────────
// This component is UNCHANGED from the original.  All React Query caching is
// encapsulated inside CompanyContext (via useCompanyProfileQuery and
// useCompanyMutations).  The component continues to consume the context through
// the same useCompany() hook with the same method signatures:
//
//   companyProfile        — the cached profile (null while first-loading)
//   loading               — true only on the initial fetch (no stale flash)
//   hasCompanyProfile     — true once the document exists
//   updateCompanyProfile  — internally calls update.mutateAsync + invalidates cache
//   createCompanyProfile  — internally calls create.mutateAsync + invalidates cache
//   refreshCompanyProfile — internally calls queryClient.invalidateQueries
//
// No imports, hooks, or JSX needed changing here.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Fade,
  Tooltip,
  Switch,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Percent as PercentIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Tag as TagIcon,
  MyLocation as MyLocationIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  AccountBalance as AccountBalanceIcon,
  Map as MapIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoCameraIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { CompanyProfile as CompanyProfileType } from './companyService';
import { countries, getCitiesByCountryName } from '../../../enums/countries';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, functions } from '../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './leaflet-fixes.css';
import ToastNotification from '../common/Toastnotification';
import { useThemeMode } from '../../../theme/Thememodecontext';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

// ─── Types ────────────────────────────────────────────────────────────────────

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCIES = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'LKR', label: 'LKR - Sri Lankan Rupee' },
];

const BUSINESS_TYPES = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Partnership (LP)',
  'Limited Liability Partnership (LLP)',
  'Limited Liability Company (LLC)',
  'S Corporation',
  'C Corporation',
  'Benefit Corporation (B Corp)',
  'Cooperative',
  'Non-Profit Organization',
  'Other',
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Colombo', label: 'Sri Lanka (SLST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

const SectionHeader: React.FC<{
  icon: React.ReactElement;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ icon, title, subtitle, action }) => (
  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} sx={{ mb: 3 }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 1.5,
          bgcolor: 'primary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color: 'primary.contrastText', fontSize: 18 } } as object)}
      </Box>
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary" lineHeight={1.2}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Stack>
);

const Section: React.FC<{ children: React.ReactNode; id?: string }> = ({ children, id }) => (
  <Paper
    id={id}
    elevation={0}
    sx={{
      mb: 3,
      p: { xs: 2.5, sm: 3.5 },
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 2 },
    }}
  >
    {children}
  </Paper>
);

// ─── Firebase Usage (Spark plan) ────────────────────────────────────────────

const formatBytes = (bytes: number): string => {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const formatCount = (n: number): string => n.toLocaleString('en-US');

interface UsageMetric { value: number; available: boolean; }
interface FirebaseUsageResponse {
  fetchedAt: string;
  metrics: {
    firestoreReadsToday: UsageMetric;
    firestoreWritesToday: UsageMetric;
    firestoreDeletesToday: UsageMetric;
    storageStoredBytes: UsageMetric;
    storageDownloadedBytesToday: UsageMetric;
    functionInvocationsThisMonth: UsageMetric;
  };
  limits: {
    firestoreReadsPerDay: number;
    firestoreWritesPerDay: number;
    firestoreDeletesPerDay: number;
    firestoreStorageBytes: number;
    storageBytes: number;
    storageDownloadBytesPerDay: number;
    functionsInvocationsPerMonth: number;
  };
}

const getFirebaseUsageFn = httpsCallable<Record<string, never>, FirebaseUsageResponse>(
  functions,
  'getFirebaseUsage'
);

const UsageBar: React.FC<{ label: string; metric: UsageMetric; limit: number; format: (n: number) => string }> = ({
  label, metric, limit, format,
}) => {
  const pct = metric.available ? Math.min(100, (metric.value / limit) * 100) : 0;
  const color = pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary';
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {metric.available ? `${format(metric.value)} / ${format(limit)}` : 'Unavailable'}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={metric.available ? pct : 0}
        color={color as 'primary' | 'warning' | 'error'}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  );
};

const FirebaseUsageCard: React.FC = () => {
  const [data, setData] = useState<FirebaseUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getFirebaseUsageFn();
      setData(res.data);
    } catch (err: any) {
      setError(err?.message || 'Could not load Firebase usage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  return (
    <Section>
      <SectionHeader
        icon={<StorageIcon />}
        title="Firebase Usage"
        subtitle="Spark (free tier) plan usage vs. limits"
        action={
          <IconButton size="small" onClick={fetchUsage} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        }
      />
      {error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
          {error} This can happen if the deployed function's service account doesn't have the "Monitoring Viewer" role yet.
        </Alert>
      )}
      {!error && loading && !data && (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={28} />
        </Stack>
      )}
      {data && (
        <Stack spacing={2.5}>
          <UsageBar label="Firestore reads (today)" metric={data.metrics.firestoreReadsToday} limit={data.limits.firestoreReadsPerDay} format={formatCount} />
          <UsageBar label="Firestore writes (today)" metric={data.metrics.firestoreWritesToday} limit={data.limits.firestoreWritesPerDay} format={formatCount} />
          <UsageBar label="Firestore deletes (today)" metric={data.metrics.firestoreDeletesToday} limit={data.limits.firestoreDeletesPerDay} format={formatCount} />
          <UsageBar label="Cloud Storage stored" metric={data.metrics.storageStoredBytes} limit={data.limits.storageBytes} format={formatBytes} />
          <UsageBar label="Cloud Storage downloaded (today)" metric={data.metrics.storageDownloadedBytesToday} limit={data.limits.storageDownloadBytesPerDay} format={formatBytes} />
          <UsageBar label="Function invocations (this month)" metric={data.metrics.functionInvocationsThisMonth} limit={data.limits.functionsInvocationsPerMonth} format={formatCount} />
          <Typography variant="caption" color="text.secondary">
            Last updated {new Date(data.fetchedAt).toLocaleString()}. Figures come from Google Cloud Monitoring and may lag actual usage by a few minutes.
          </Typography>
        </Stack>
      )}
    </Section>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const CompanyProfile: React.FC = () => {
  const { currentUser, isSuperAdmin, userProfile } = useAuth(); const {
    companyProfile,
    loading: contextLoading,
    hasCompanyProfile,
    updateCompanyProfile,
    createCompanyProfile,
    refreshCompanyProfile,
  } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isDark } = useThemeMode();

  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerInstanceRef = useRef<L.Marker | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  const [formData, setFormData] = useState<CompanyProfileType>({
    companyName: '',
    businessType: '',
    registrationNumber: '',
    taxId: '',
    address: { street: '', city: '', state: '', postalCode: '', country: '' },
    contactInfo: { phone: '', email: '', website: '', fax: '' },
    socialMedia: { facebook: '', instagram: '', threads: '' },
    location: { latitude: 6.9271, longitude: 79.8612, address: '' },
    currency: 'USD',
    timezone: 'America/New_York',
    fiscalYearStart: '01-01',
    taxPercentage: 0,
    logo: '',
    notes: '',
    displayPrices: true,
  });

  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((p) => ({ ...p, open: false }));

  const updateLocationFromLatLng = useCallback(async (lat: number, lng: number) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const address = res.ok ? (await res.json()).display_name || '' : '';
      setFormData((p) => ({ ...p, location: { latitude: lat, longitude: lng, address } }));
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setPopupContent(address || `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
        markerInstanceRef.current.openPopup();
      }
    } catch {
      setFormData((p) => ({ ...p, location: { latitude: lat, longitude: lng, address: '' } }));
    }
  }, []);

  const destroyMap = useCallback(() => {
    try { markerInstanceRef.current?.remove(); } catch { }
    try { mapInstanceRef.current?.remove(); mapInstanceRef.current?.off(); } catch { }
    markerInstanceRef.current = null;
    mapInstanceRef.current = null;
    if (mapRef.current) mapRef.current.innerHTML = '';
  }, []);

  const initializeMap = useCallback(() => {
    if (!mapRef.current) return;
    setMapLoading(true);
    destroyMap();
    setTimeout(() => {
      if (!mapRef.current) { setMapLoading(false); return; }
      try {
        const mapInstance = L.map(mapRef.current, {
          attributionControl: true, zoomControl: true,
          minZoom: 2, maxZoom: 19, preferCanvas: true,
        }).setView([formData.location.latitude, formData.location.longitude], 15);

        L.tileLayer(
          isDark
            ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
            : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            attribution: isDark
              ? '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
            subdomains: isDark ? 'abcd' : 'abc',
          }
        ).addTo(mapInstance);

        const markerInstance = L.marker(
          [formData.location.latitude, formData.location.longitude],
          { draggable: editing, title: 'Company Location' }
        ).addTo(mapInstance);

        const popupContent = formData.location.address ||
          `Lat: ${formData.location.latitude.toFixed(6)}, Lng: ${formData.location.longitude.toFixed(6)}`;
        markerInstance.bindPopup(popupContent).openPopup();

        mapInstance.on('click', (e: L.LeafletMouseEvent) => {
          if (editing) {
            const { lat, lng } = e.latlng;
            markerInstance.setLatLng([lat, lng]);
            updateLocationFromLatLng(lat, lng);
          }
        });

        markerInstance.on('dragend', (e: L.DragEndEvent) => {
          const { lat, lng } = e.target.getLatLng();
          updateLocationFromLatLng(lat, lng);
        });

        mapInstanceRef.current = mapInstance;
        markerInstanceRef.current = markerInstance;

        setTimeout(() => {
          mapInstanceRef.current?.invalidateSize();
          setMapLoading(false);
        }, 300);
      } catch { setMapLoading(false); showToast('Failed to initialize map', 'error'); }
    }, 200);
  }, [formData.location.latitude, formData.location.longitude, formData.location.address, editing, updateLocationFromLatLng, destroyMap, isDark]);

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) { showToast('Please enter a location to search', 'warning'); return; }
    try {
      setSearching(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      );
      if (res.ok) {
        const data = await res.json();
        if (data?.length > 0) {
          const loc = data[0];
          const lat = parseFloat(loc.lat), lng = parseFloat(loc.lon);
          setFormData((p) => ({ ...p, location: { latitude: lat, longitude: lng, address: loc.display_name || '' } }));
          if (mapInstanceRef.current && markerInstanceRef.current) {
            mapInstanceRef.current.setView([lat, lng], 15);
            markerInstanceRef.current.setLatLng([lat, lng]);
            markerInstanceRef.current.setPopupContent(loc.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            markerInstanceRef.current.openPopup();
          }
          setSearchQuery('');
          showToast('Location found!', 'success');
        } else {
          showToast('Location not found. Try a different search term.', 'warning');
        }
      }
    } catch { showToast('Search failed. Please try again.', 'error'); }
    finally { setSearching(false); }
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) { showToast('Geolocation not supported', 'error'); return; }
    showToast('Getting your location…', 'info');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        setFormData((p) => ({ ...p, location: { ...p.location, latitude: lat, longitude: lng } }));
        if (mapInstanceRef.current && markerInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 15);
          markerInstanceRef.current.setLatLng([lat, lng]);
          updateLocationFromLatLng(lat, lng);
        }
        showToast('Location detected!', 'success');
      },
      (err) => showToast('Unable to get location: ' + err.message, 'error')
    );
  };

  useEffect(() => {
    const t = setTimeout(() => initializeMap(), 100);
    return () => clearTimeout(t);
  }, [mapKey, initializeMap]);

  useEffect(() => () => destroyMap(), [destroyMap]);

  useEffect(() => {
    if (mapInstanceRef.current && markerInstanceRef.current) {
      const ll: L.LatLngExpression = [formData.location.latitude, formData.location.longitude];
      markerInstanceRef.current.setLatLng(ll);
      mapInstanceRef.current.setView(ll, mapInstanceRef.current.getZoom());
      markerInstanceRef.current.setPopupContent(
        formData.location.address || `Lat: ${formData.location.latitude.toFixed(6)}, Lng: ${formData.location.longitude.toFixed(6)}`
      );
    }
  }, [formData.location.latitude, formData.location.longitude, formData.location.address]);

  useEffect(() => { if (companyProfile) setFormData(companyProfile); }, [companyProfile]);
  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      if (parent === 'address' && child === 'country') {
        setFormData((p) => ({ ...p, address: { ...p.address, country: value, state: '', city: '' } }));
        return;
      }
      setFormData((p) => ({ ...p, [parent]: { ...(p[parent as keyof CompanyProfileType] as any), [child]: value } }));
    } else if (name === 'taxPercentage') {
      const n = value === '' ? 0 : parseFloat(value);
      setFormData((p) => ({ ...p, taxPercentage: isNaN(n) ? 0 : n }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select a valid image file', 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showToast('Logo must be less than 2MB', 'error'); return; }
    setSelectedLogo(file);
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoPreview(URL.createObjectURL(file));
    showToast('Logo selected', 'success');
  };

  const handleUploadLogo = async (): Promise<string | null> => {
    if (!selectedLogo || !currentUser) return null;
    try {
      setUploadingLogo(true);
      const ext = selectedLogo.name.split('.').pop();
      const storageRef = ref(storage, `company_logos/company_logo_${Date.now()}.${ext}`);
      await uploadBytes(storageRef, selectedLogo);
      const url = await getDownloadURL(storageRef);
      if (formData.logo?.includes('firebase')) {
        try { await deleteObject(ref(storage, formData.logo)); } catch { }
      }
      return url;
    } catch (err: any) {
      throw new Error('Failed to upload logo: ' + err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setSelectedLogo(null);
    if (logoPreview) { URL.revokeObjectURL(logoPreview); setLogoPreview(''); }
    if (logoInputRef.current) logoInputRef.current.value = '';
    setFormData((p) => ({ ...p, logo: '' }));
    showToast('Logo removed', 'info');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) { showToast('You must be logged in', 'error'); return; }
    if (!formData.companyName.trim()) { showToast('Company name is required', 'error'); return; }
    if (!formData.contactInfo.email.trim()) { showToast('Contact email is required', 'error'); return; }
    if (formData.taxPercentage < 0 || formData.taxPercentage > 100) { showToast('Tax % must be 0–100', 'error'); return; }
    const urlPattern = /^https?:\/\/.+/i;
    for (const [key, val] of Object.entries(formData.socialMedia)) {
      if (val && !urlPattern.test(val)) { showToast(`${key} URL must start with http:// or https://`, 'error'); return; }
    }
    try {
      setSaving(true);
      let logoURL = formData.logo;
      if (selectedLogo) { showToast('Uploading logo…', 'info'); const u = await handleUploadLogo(); if (u) logoURL = u; }
      const data = { ...formData, logo: logoURL };
      if (hasCompanyProfile) {
        await updateCompanyProfile(data);
        showToast('Company profile updated!', 'success');
      } else {
        await createCompanyProfile(data);
        showToast('Company profile created!', 'success');
      }
      setEditing(false);
      setSelectedLogo(null);
      if (logoPreview) { URL.revokeObjectURL(logoPreview); setLogoPreview(''); }
      setMapKey((p) => p + 1);
    } catch (err: any) {
      showToast(err.message || 'Failed to save company profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setEditing(false);
    setSelectedLogo(null);
    if (logoPreview) { URL.revokeObjectURL(logoPreview); setLogoPreview(''); }
    if (companyProfile) setFormData(companyProfile);
    setMapKey((p) => p + 1);
    showToast('Changes cancelled', 'info');
  };

  const handleEditClick = () => { setEditing(true); setMapKey((p) => p + 1); };

  // ─── Guards ────────────────────────────────────────────────────────────────

  if (contextLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading company profile…</Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please log in to manage the company profile.</Alert>
      </Box>
    );
  }

  const logoSrc = logoPreview || formData.logo;
  const companyInitial = formData.companyName?.[0]?.toUpperCase() || 'C';

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        bgcolor: 'background.default',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: '3px' },
      }}
    >
      {/* ── Hero Banner — identical structure to UserProfile ─────────────── */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          pt: { xs: 4, sm: 5 },
          pb: { xs: 8, sm: 10 },
          px: { xs: 2.5, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""', position: 'absolute',
            width: 280, height: 280, borderRadius: '50%',
            bgcolor: 'primary.light', opacity: 0.15,
            top: -80, right: -60,
          },
          '&::after': {
            content: '""', position: 'absolute',
            width: 180, height: 180, borderRadius: '50%',
            bgcolor: 'primary.dark', opacity: 0.15,
            bottom: -50, left: 30,
          },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto', position: 'relative', zIndex: 1 }}>
          {/* Title row */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <BusinessIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: 'primary.contrastText' }} />
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="primary.contrastText">
                Company Profile
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
                Manage your company information and business settings
              </Typography>
            </Box>
          </Stack>

          {/* Stats row — mirrors UserProfile stats */}
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {[
              { label: 'Type', value: formData.businessType || '—' },
              { label: 'Currency', value: formData.currency || '—' },
              { label: 'Tax', value: formData.taxPercentage != null ? `${formData.taxPercentage}%` : '—' },
            ].map((s) => (
              <Grid item xs={4} key={s.label}>
                <Box
                  sx={{
                    bgcolor: 'primary.dark',
                    opacity: 0.85,
                    borderRadius: 1,
                    p: { xs: 1, sm: 1.5 },
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="primary.contrastText"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '0.78rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.75 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Content area — mt: -5 pulls cards up over the hero bottom padding ── */}
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, mt: -5, pb: 5 }}>

        {!hasCompanyProfile && !editing && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            No company profile found. Click <strong>"Edit Profile"</strong> to get started.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── Company Identity Card — mirrors UserProfile profile card ── */}
          <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>

            {/* Logo strip — identical pattern to UserProfile avatar strip */}
            <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: { xs: 2.5, sm: 3 }, pb: 2.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'center', sm: 'flex-end' }}
                spacing={2}
              >
                {/* ── Company logo avatar ── */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar
                    src={logoSrc || undefined}
                    variant="rounded"
                    sx={{
                      width: { xs: 88, sm: 100 },
                      height: { xs: 88, sm: 100 },
                      border: '4px solid',
                      borderColor: 'background.paper',
                      boxShadow: 3,
                      bgcolor: 'primary.main',
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      borderRadius: 3,
                    }}
                  >
                    {/* Fallback: company initial when no logo */}
                    {!logoSrc && companyInitial}
                  </Avatar>

                  {/* Camera / upload button shown in edit mode */}
                  {editing && (
                    <Tooltip title="Upload logo (max 2MB)">
                      <IconButton
                        size="small"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={saving || uploadingLogo}
                        sx={{
                          position: 'absolute', bottom: 2, right: 2,
                          bgcolor: 'primary.main', color: 'primary.contrastText',
                          width: 28, height: 28,
                          '&:hover': { bgcolor: 'primary.dark' },
                          boxShadow: 2,
                        }}
                      >
                        {uploadingLogo
                          ? <CircularProgress size={14} color="inherit" />
                          : <PhotoCameraIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Remove button — only when editing and a logo exists */}
                  {editing && logoSrc && (
                    <Tooltip title="Remove logo">
                      <IconButton
                        size="small"
                        onClick={handleRemoveLogo}
                        disabled={saving || uploadingLogo}
                        sx={{
                          position: 'absolute', top: 2, right: 2,
                          bgcolor: 'error.main', color: 'white',
                          width: 22, height: 22,
                          '&:hover': { bgcolor: 'error.dark' },
                          boxShadow: 1,
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Company name, email & type chips */}
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {formData.companyName || 'Company Name'}
                  </Typography>
                  {formData.contactInfo.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', mb: 1 }}>
                      {formData.contactInfo.email}
                    </Typography>
                  )}
                  <Stack
                    direction="row" spacing={0.75} flexWrap="wrap" useFlexGap
                    justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  >
                    {formData.businessType && (
                      <Chip label={formData.businessType} color="primary" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {formData.currency && (
                      <Chip label={formData.currency} variant="outlined" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                  </Stack>
                </Box>

                {/* Edit / Create button (only visible when not editing) */}
                {!editing && (isSuperAdmin || (currentUser && (userProfile?.permissions ?? []).includes('settings.edit'))) && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleEditClick}
                    variant="contained"
                    size="small"
                    fullWidth={isMobile}
                    color="primary"
                  >
                    {hasCompanyProfile ? 'Edit Profile' : 'Create Profile'}
                  </Button>
                )}

                {/* Selected logo filename feedback */}
                {editing && selectedLogo && (
                  <Fade in>
                    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                      ✓ {selectedLogo.name} ready to upload
                    </Typography>
                  </Fade>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Basic identity fields */}
            <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 2.5, pb: 3 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="Company Name" name="companyName"
                    value={formData.companyName} onChange={handleInputChange}
                    disabled={!editing || saving} required
                    placeholder="Enter company name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth select label="Business Type" name="businessType"
                    value={formData.businessType} onChange={handleInputChange}
                    disabled={!editing || saving}
                  >
                    {BUSINESS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="Registration Number" name="registrationNumber"
                    value={formData.registrationNumber} onChange={handleInputChange}
                    disabled={!editing || saving} placeholder="Business registration number"
                    InputProps={{ startAdornment: <InputAdornment position="start"><AccountBalanceIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth label="Tax ID / EIN" name="taxId"
                    value={formData.taxId} onChange={handleInputChange}
                    disabled={!editing || saving} placeholder="Tax identification number"
                    InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon fontSize="small" color="action" /></InputAdornment> }}
                  />
                </Grid>
              </Grid>

              {/* Hidden file input */}
              <input
                ref={logoInputRef} type="file" accept="image/*"
                onChange={handleLogoSelect} style={{ display: 'none' }}
              />
            </Box>
          </Paper>

          {/* ── 2. Address ─────────────────────────────────────────────── */}
          <Section>
            <SectionHeader icon={<LocationIcon />} title="Address Information" subtitle="Physical business location" />
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Street Address" name="address.street"
                  value={formData.address.street} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="Enter street address"
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="Country" name="address.country"
                  value={formData.address.country} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="Select country"
                >
                  {formData.address.country && !countries.some((c) => c.name === formData.address.country) && (
                    <MenuItem value={formData.address.country}>{formData.address.country}</MenuItem>
                  )}
                  {countries.map((c) => <MenuItem key={c.code} value={c.name}>{c.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="City" name="address.city"
                  value={formData.address.city} onChange={handleInputChange}
                  disabled={!editing || saving || !formData.address.country}
                  placeholder="Select city"
                >
                  {(() => {
                    const cities = getCitiesByCountryName(formData.address.country);
                    const options = formData.address.city && !cities.includes(formData.address.city)
                      ? [formData.address.city, ...cities]
                      : cities;
                    return options.map((city) => <MenuItem key={city} value={city}>{city}</MenuItem>);
                  })()}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="State / Province" name="address.state"
                  value={formData.address.state} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="Enter state or province"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Postal / ZIP Code" name="address.postalCode"
                  value={formData.address.postalCode} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="Enter postal code"
                />
              </Grid>
            </Grid>
          </Section>

          {/* ── 3. Contact Information ─────────────────────────────────── */}
          <Section>
            <SectionHeader icon={<PhoneIcon />} title="Contact Information" subtitle="How customers and partners reach you" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Phone Number" name="contactInfo.phone"
                  value={formData.contactInfo.phone} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="+1 (555) 123-4567"
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Email Address" name="contactInfo.email"
                  type="email" value={formData.contactInfo.email} onChange={handleInputChange}
                  disabled={!editing || saving} required placeholder="company@example.com"
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Website" name="contactInfo.website"
                  value={formData.contactInfo.website} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="https://www.example.com"
                  InputProps={{ startAdornment: <InputAdornment position="start"><LanguageIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Fax Number" name="contactInfo.fax"
                  value={formData.contactInfo.fax} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="+1 (555) 123-4568"
                />
              </Grid>
            </Grid>
          </Section>

          {/* ── 4. Social Media ────────────────────────────────────────── */}
          <Section>
            <SectionHeader icon={<ShareIcon />} title="Social Media Links" subtitle="Connect your social presence" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth label="Facebook URL" name="socialMedia.facebook"
                  value={formData.socialMedia.facebook} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="https://facebook.com/yourpage"
                  helperText="Full Facebook page URL"
                  InputProps={{ startAdornment: <InputAdornment position="start"><FacebookIcon sx={{ color: '#1877F2', fontSize: 20 }} /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth label="Instagram URL" name="socialMedia.instagram"
                  value={formData.socialMedia.instagram} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="https://instagram.com/yourpage"
                  helperText="Full Instagram profile URL"
                  InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon sx={{ color: '#E4405F', fontSize: 20 }} /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth label="Threads URL" name="socialMedia.threads"
                  value={formData.socialMedia.threads} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="https://threads.net/@yourpage"
                  helperText="Full Threads profile URL"
                  InputProps={{ startAdornment: <InputAdornment position="start"><TagIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
            </Grid>
          </Section>

          {/* ── 5. Location Map ────────────────────────────────────────── */}
          <Section>
            <SectionHeader icon={<MapIcon />} title="Company Location" subtitle="Set your physical location on the map" />

            {editing && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2.5 }} alignItems="flex-start">
                <TextField
                  fullWidth placeholder="Search for a location…"
                  size="small" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearchLocation())}
                  disabled={searching}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                />
                <Button
                  variant="outlined" size="small"
                  startIcon={searching ? <CircularProgress size={14} /> : <SearchIcon />}
                  onClick={handleSearchLocation} disabled={searching || !searchQuery.trim()}
                  sx={{ flexShrink: 0, minWidth: 110 }}
                >
                  {searching ? 'Searching…' : 'Search'}
                </Button>
                <Button
                  variant="outlined" size="small" color="secondary"
                  startIcon={<MyLocationIcon />}
                  onClick={handleGetCurrentLocation}
                  sx={{ flexShrink: 0, minWidth: 140 }}
                >
                  My Location
                </Button>
              </Stack>
            )}

            <Box
              key={`map-${mapKey}`}
              sx={{
                width: '100%', height: 380,
                borderRadius: 2, overflow: 'hidden',
                border: '1px solid', borderColor: 'divider',
                mb: 2, position: 'relative',
              }}
            >
              {mapLoading && (
                <Box sx={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'background.paper', zIndex: 1000,
                }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" color="text.secondary">Loading map…</Typography>
                  </Stack>
                </Box>
              )}
              {!mapLoading && mapInstanceRef.current && (
                <Box
                  sx={{
                    position: 'absolute', top: 10, left: 10, zIndex: 1001,
                    bgcolor: editing ? 'warning.main' : 'success.main',
                    color: 'white', px: 1.5, py: 0.5, borderRadius: 1,
                    fontSize: '0.75rem', fontWeight: 700, boxShadow: 2,
                  }}
                >
                  {editing ? '📍 Edit — Click to set location' : '📍 View Mode'}
                </Box>
              )}
              <div ref={mapRef} className={isDark ? 'company-map-dark' : undefined} style={{ width: '100%', height: '100%' }} />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Latitude" value={formData.location.latitude.toFixed(6)} disabled size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Longitude" value={formData.location.longitude.toFixed(6)} disabled size="small" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Formatted Address" value={formData.location.address} disabled size="small" />
              </Grid>
            </Grid>

            {editing && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
                Click the map or drag the marker to position your company location. You can also search or use your current location.
              </Alert>
            )}
          </Section>

          {/* ── 6. Business Settings ───────────────────────────────────── */}
          <Section>
            <SectionHeader icon={<SettingsIcon />} title="Business Settings" subtitle="Currency, timezone and fiscal configuration" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="Default Currency" name="currency"
                  value={formData.currency} onChange={handleInputChange}
                  disabled={!editing || saving}
                  InputProps={{ startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" color="action" /></InputAdornment> }}
                >
                  {CURRENCIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="Timezone" name="timezone"
                  value={formData.timezone} onChange={handleInputChange}
                  disabled={!editing || saving}
                  InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon fontSize="small" color="action" /></InputAdornment> }}
                >
                  {TIMEZONES.map((tz) => <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Fiscal Year Start" name="fiscalYearStart"
                  value={formData.fiscalYearStart} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="MM-DD"
                  helperText="Format: MM-DD (e.g. 01-01 for January 1st)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Default Tax Percentage" name="taxPercentage"
                  type="number" value={formData.taxPercentage} onChange={handleInputChange}
                  disabled={!editing || saving} placeholder="0.00"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  helperText="Used as default tax on invoices (0–100)"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PercentIcon fontSize="small" color="action" /></InputAdornment>,
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Switch
                    checked={formData.displayPrices !== false}
                    onChange={(e) => setFormData((p) => ({ ...p, displayPrices: e.target.checked }))}
                    disabled={!editing || saving}
                  />
                  <Box>
                    <Typography variant="body1">Display prices on the website</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      When off, vehicle prices are hidden on the client website and customers are prompted to contact you instead.
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Notes" name="notes"
                  value={formData.notes} onChange={handleInputChange}
                  disabled={!editing || saving} multiline rows={3}
                  placeholder="Additional notes or information about the company"
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon fontSize="small" color="action" /></InputAdornment> }}
                />
              </Grid>
            </Grid>
          </Section>

          <FirebaseUsageCard />

          {/* ── Sticky Action Bar ──────────────────────────────────────── */}
          {editing && (
            <Paper
              elevation={0}
              sx={{
                position: 'sticky', bottom: 16,
                p: 2, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'background.paper',
                backdropFilter: 'blur(8px)',
                zIndex: 10,
              }}
            >
              <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                <Button
                  onClick={handleCancel} disabled={saving || uploadingLogo}
                  startIcon={<CancelIcon />}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  type="submit" variant="contained" color="primary"
                  disabled={saving || uploadingLogo}
                  fullWidth={isMobile}
                  startIcon={saving || uploadingLogo ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                >
                  {uploadingLogo ? 'Uploading…' : saving ? 'Saving…' : hasCompanyProfile ? 'Save Changes' : 'Create Profile'}
                </Button>
              </Stack>
            </Paper>
          )}

        </form>
      </Box>

      <ToastNotification open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />
    </Box>
  );
};

export default CompanyProfile;